'use client';

import { ControlType, CalculatorId, CalculatorType, ControlIds, type CalculatorConfig, type ControlValues, type CalculatorControl } from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';
import { ReactNode } from 'react';
import { barGraphDescription } from './bar-graph.description';

// Type for bar graph specific control values
export interface BarGraphControlValues {
  category?: string;
  values?: string[];
  barLayout?: 'stacked' | 'grouped';
  orientation?: 'vertical' | 'horizontal';
  title?: string;
  barWidth?: number;
  enableShadow?: boolean;
}

// Define the specific controls used in the bar graph
export const barGraphControls = [
  {
    id: ControlIds.Values,
    type: ControlType.MultiColumnSelect,
    label: 'Value columns',
    numericOnly: true,
    minSelections: 1,
    required: true
  },
  {
    id: ControlIds.Category,
    type: ControlType.SingleColumnSelect,
    label: 'Category column',
    required: true
  },
  {
    id: ControlIds.Title,
    type: ControlType.Text,
    label: 'Chart title'
  },
  {
    id: ControlIds.BarLayout,
    type: ControlType.Select,
    label: 'Bar layout',
    options: [
      { label: 'Stacked', value: 'stacked' },
      { label: 'Grouped', value: 'grouped' }
    ],
    defaultValue: 'stacked'
  },
  {
    id: ControlIds.Orientation,
    type: ControlType.Select,
    label: 'Bar orientation',
    options: [
      { label: 'Vertical', value: 'vertical' },
      { label: 'Horizontal', value: 'horizontal' }
    ],
    defaultValue: 'vertical'
  },
  {
    id: ControlIds.BarWidth,
    type: ControlType.Number,
    label: 'Bar width',
    min: 10,
    max: 100,
    step: 1,
    defaultValue: 40
  },
  {
    id: ControlIds.EnableShadow,
    type: ControlType.Select,
    label: 'Bar emphasis',
    options: [
      { label: 'None', value: 'false' },
      { label: 'Shadow', value: 'true' }
    ],
    defaultValue: 'true'
  }
] satisfies readonly CalculatorControl[];

const isNumericColumn = (data: TableDataJSON, column: { values: string[] }): boolean => {
  return column.values.every(val => !isNaN(Number(val)));
};

export const getInitialConfig = (data: TableDataJSON): Partial<BarGraphControlValues> => {
  const config: Partial<BarGraphControlValues> = {
    orientation: 'vertical',
    barLayout: 'stacked',
    barWidth: 40,
    title: 'Bar Graph'
  };

  // Only set category and values if we have valid data
  if (data.columns.length > 0) {
    const numericColumns = data.columns.filter(col => isNumericColumn(data, col));
    const nonNumericColumns = data.columns.filter(col => !isNumericColumn(data, col));

    // If we have non-numeric columns, use the first one as category
    if (nonNumericColumns.length > 0 && nonNumericColumns[0]?.id) {
      config.category = nonNumericColumns[0].id;
      if (numericColumns.length > 0 && numericColumns[0]?.id) {
        config.values = [numericColumns[0].id];
      }
    } 
    // If all columns are numeric, use first as category and second as value
    else if (numericColumns.length >= 2 && numericColumns[0]?.id && numericColumns[1]?.id) {
      config.category = numericColumns[0].id;
      config.values = [numericColumns[1].id];
    }

    // Set a more descriptive title if we have category and value columns
    if (config.category && config.values?.length) {
      const categoryCol = data.columns.find(col => col.id === config.category);
      const valuesCols = config.values.map(id => data.columns.find(col => col.id === id)).filter(Boolean);
      if (categoryCol && valuesCols.length) {
        config.title = `${valuesCols.map(col => col?.name).join(' and ')} by ${categoryCol.name}`;
      }
    }
  }

  return config;
};

/**
 * Create ECharts configuration for bar graph
 * This is a pure function that can be tested in isolation
 */
export function getBarGraphConfig({
  categories,
  series,
  categoryName,
  title,
  barWidth,
  barColor,
  orientation,
  barLayout,
  enableShadow
}: {
  categories: string[]
  series: { name: string; values: number[] }[]
  categoryName: string
  title?: string
  barWidth?: number
  barColor?: string
  orientation?: string
  barLayout?: 'stacked' | 'grouped'
  enableShadow?: boolean
}): EChartsOption {
  const isHorizontal = orientation === 'horizontal';

  const seriesConfig = series.map(s => ({
    name: s.name,
    type: 'bar' as const,
    data: s.values,
    stack: barLayout === 'stacked' ? 'total' : undefined,
    barWidth: barWidth,
    barGap: barLayout === 'grouped' ? '30%' : '0%'
  }));

  return {
    title: title ? {
      text: title,
      top: 0,
      left: 'center'
    } : undefined,
    tooltip: {
      trigger: 'axis',
      axisPointer: enableShadow ? {
        type: 'shadow'
      } : undefined
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'bar-graph'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    legend: {
      top: 25,
      data: series.map(s => s.name)
    },
    grid: {
      top: 90,
      left: 30,
      right: 30,
      bottom: 30,
      containLabel: true
    },
    [isHorizontal ? 'yAxis' : 'xAxis']: {
      type: 'category',
      data: categories,
      axisLabel: {
        interval: 0,
        rotate: isHorizontal ? 0 : 30
      }
    },
    [isHorizontal ? 'xAxis' : 'yAxis']: {
      type: 'value'
    },
    series: seriesConfig
  };
}

export const barGraphConfig: CalculatorConfig<CalculatorId.BarGraph> = {
  id: CalculatorId.BarGraph,
  type: CalculatorType.Graph,
  name: 'Bar Graph',
  initialData: TableDataJSON.from({
    columns: [
      { id: 'brand', name: 'Brand', values: ['Toyota', 'Honda', 'Ford'] },
      { id: 'mpg', name: 'MPG', values: ['30', '28', '25'] },
      { id: 'horsepower', name: 'Horsepower', values: ['180', '170', '200'] }
    ]
  }),
  initialControlValues: {
    category: 'brand',
    values: ['mpg'],
    barLayout: 'grouped',
    orientation: 'vertical',
    title: 'MPG by Brand',
    barWidth: 40,
    enableShadow: 'true'
  },
  controls: {
    tabs: [
      {
        title: 'Data',
        controls: barGraphControls.slice(0, 2)
      },
      {
        title: 'Appearance',
        controls: [...barGraphControls.slice(2)]
      }
    ]
  },
  getInitialConfig,
  getVisualizationData: ({ data, controls }) => {
    // Type assert the controls to our specific type
    const barGraphControls = controls as BarGraphControlValues;
    
    if (!data?.columns?.length || !barGraphControls.category || !barGraphControls.values?.length) {
      console.log('Missing data or controls:', { data, controls });
      return null;
    }

    // Get unique categories
    const categoryColumn = data.columns.find(col => col.id === barGraphControls.category);
    if (!categoryColumn) {
      console.log('Category column not found:', barGraphControls.category);
      return null;
    }
    
    const categories = [...new Set(categoryColumn.values)];
    
    // Create series data for each selected value column
    const series = barGraphControls.values.map(valueId => {
      const column = data.columns.find(col => col.id === valueId);
      if (!column) {
        console.log('Value column not found:', valueId);
        return undefined;
      }

      // For each category, find the corresponding value
      const seriesData = categories.map(category => {
        const categoryIndices = categoryColumn.values
          .map((val, idx) => val === category ? idx : -1)
          .filter(idx => idx !== -1);
        
        // If multiple values exist for this category, take their average
        const values = categoryIndices.map(idx => Number(column.values[idx]) || 0);
        const average = values.reduce((sum, val) => sum + val, 0) / values.length;
        return isNaN(average) ? 0 : average;
      });

      return {
        name: column.name,
        values: seriesData
      };
    }).filter((s): s is NonNullable<typeof s> => s !== undefined);

    if (series.length === 0) {
      console.log('No valid series data generated');
      return null;
    }

    return getBarGraphConfig({
      categories,
      series,
      categoryName: categoryColumn.name,
      title: barGraphControls.title,
      barWidth: barGraphControls.barWidth,
      orientation: barGraphControls.orientation,
      barLayout: barGraphControls.barLayout,
      enableShadow: barGraphControls.enableShadow
    });
  },
  render: ({ visualizationData }) => {
    if (!visualizationData) {
      return (
        <div className="flex items-center justify-center w-full h-full border border-dashed border-gray-300 rounded-lg">
          No data to visualize
        </div>
      );
    }

    return (
      <LazyTabLabECharts
        option={visualizationData}
        className="w-full h-full"
        themeName="soft-cosmic-flare"
      />
    );
  }
}; 